import { useEffect, useState } from 'react';
import { Button, Groupper, Header, Message, Modal, Table, Toast, Toolbar } from 'react-frontier';
import { useParams } from 'react-router-dom';
import { InventoryType, InventoryWarehouse, SetLoading } from '../SiteClasses';
import { bindClick, formatFullAddressMX } from '../SiteUtil';
import { useBreadcrumbs, useUser } from '../hooks';
import { CreateWarehouseModal, LocationMap, InventoryItemsByType, WarehouseInventory } from '../components';
import UserAccess from '../UserAccess';
import moment from 'moment';
import API from '../API';

enum Modals{
	INFO = 1,
	EDIT = 2,
	DELETE = 4
}

var WarehouseView = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var { warehouseId, typeId } = useParams();
	var warehouse_id = warehouseId ? parseInt(warehouseId) : null;
	var type_id = typeId ? parseInt(typeId) : null;

	var [warehouse, setWarehouse] = useState<InventoryWarehouse | null>(null);
	var [loadError, setLoadError] = useState<string>(null);

	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);

	useEffect(()=>{
		setTitle('Almacén ' + warehouseId.toString());
	}, []);

	useEffect(()=>{
		if(!warehouse_id) return setLoadError('Almacén no válido.');
		get();
	}, [warehouse_id]);

	var get = ()=>{
		setLoadError(null);
		API.getWarehouse(warehouse_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setWarehouse(res.data);
			setTitle(res.data.warehouse_name);
		})
		.catch(()=> setLoadError('Hubo un error cargando la información del almacén.'));
	};

	var openDeleteWarehouse = ()=>{
		setModalPrompts([]);
		setShownModal(Modals.DELETE);
	};

	var onWarehouseUpdated = (updated: Partial<InventoryWarehouse>)=>{
		setWarehouse(prev=>({ ...prev, ...updated } as InventoryWarehouse));
		setShownModal(null);
	};

	var submitDelete = (setLoadingModal: SetLoading)=>{
		if(!warehouse_id) return;
		setLoadingModal(true);
		API.deleteInventoryWarehouse(warehouse_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Almacén eliminado');
			setWarehouse(prev=>({ ...prev, deleted: 1 } as InventoryWarehouse));
			setShownModal(null);
		})
		.catch(()=> setModalPrompts(['Hubo un error eliminando el almacén.']))
		.finally(()=> setLoadingModal(false));
	};

	if(loadError) return <Header text='Error' subtext={loadError}/>;
	if(!warehouse) return <Header text='Cargando almacén' loading/>;

	var canEditWarehouse = hasAccess(UserAccess.INVENTORY.EDIT_WAREHOUSES);
	var canDeleteWarehouse = hasAccess(UserAccess.INVENTORY.DELETE_WAREHOUSES);

	return <div className='ar inventory-view-screen'>
		<div className='left'>
			<Groupper
				title='Datos del almacén'
				style={{ marginTop: 0, marginBottom: 0 }}>
				<Toolbar style={{ height: 35 }}>
					{canEditWarehouse ? (
						<Toolbar.Item
							text='Editar'
							iconName='pen'
							onClick={bindClick(setShownModal, Modals.EDIT)}
						/>
					) : null}
					{canDeleteWarehouse ? <Toolbar.Item text='Eliminar' iconName='close' onClick={openDeleteWarehouse} /> : null}
				</Toolbar>
				<Table
					fitted
					details
					striped
					className='first last'
					data={[
						['ID', warehouse.warehouse_id],
						['Nombre', warehouse.warehouse_name],
						['Descripción', warehouse.description],
						['Total equipos', warehouse.total_items],
						['Maletines', warehouse.briefcase_items ?? 0],
						['Capacidad máxima', warehouse.capacity>0?warehouse.capacity:'Sin limite'],
						null,
						['Creado', moment.unix(warehouse.date_created).format('DD/MM/YY HH:mm')],
						['Creador', warehouse.user_creator_name],
						null,
						['Dirección', formatFullAddressMX(warehouse.location)],
						null
					]}
				/>
			</Groupper>
			<Groupper
				style={{ marginTop: 0, padding: 0 }}>
				{warehouse.location && (
					<LocationMap
						location={warehouse.location}
					/>
				)}
			</Groupper>
		</div>
		<div className='right'>
			{typeId ? (
				<InventoryItemsByType warehouse_id={warehouse_id} type_id={type_id} />
			) : (
				<WarehouseInventory warehouse_id={warehouse_id}/>
			)}
		</div>
		<CreateWarehouseModal
			open={shownModal===Modals.EDIT}
			onClose={bindClick(setShownModal)}
			warehouseId={warehouse.warehouse_id}
			onUpdate={onWarehouseUpdated}
		/>
		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>¿Eliminar almacén?</Modal.Header>
			<Modal.Content>
				<Header text='¿Deseas eliminar el almacén?' subtext={'El almacén será eliminado dentro del sistema'}/>
				<Message type='error' centered>
					Se eliminará el almacén de forma permanente. Esta acción no se puede deshacer.
				</Message>
				<Message type='error' list={modalPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={bindClick(setShownModal)}/>
				<Button color='red' text='Eliminar' onClick={submitDelete}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.INFO} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>Información de almacén</Modal.Header>
			{warehouse && <Modal.Content style={{paddingBottom: 0}}>
				<Table
					striped
					details
					fitted
					data={[
						['ID', warehouse.warehouse_id],
						['Nombre', warehouse.warehouse_name],
						['Descripción', warehouse.description],
						null,
						['Creado', moment.unix(warehouse.date_created).format('DD/MM/YY HH:mm')],
						['Creador', warehouse.user_creator_name],
						null,
						['Dirección', formatFullAddressMX(warehouse.location)],
					]}
				/>
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal)}/>
			</Modal.Actions>
		</Modal>
	</div>;
};

export default WarehouseView;
